/* WHAT IS UNDER THE FINGER.
 *
 * ═══ RAYMARINE'S MODEL, WHICH IS THE RIGHT ONE ═══
 *
 * A tap does not ACT — it ASKS. Everything within a short radius of the point is
 * listed and you choose, which turns one gesture into the way you interrogate
 * the whole chart rather than every layer inventing its own tap handling and
 * half of them never getting one. `chart-core/lib/chartpick` already does the
 * hard part, including the tie-break order, which is a safety judgement rather
 * than a preference.
 *
 * ═══ WHY THIS IS A STORE NOW AND NOT A COMPONENT WITH AN EXPORTED METHOD ═══
 *
 * It used to be `Sheet.svelte`, which held the hit list in local state and
 * exposed `onTap` for the chart to call through a `bind:this`. That works when
 * exactly one component renders it. Under the panel registry the pick is a
 * registered panel like any other, mounted and unmounted by the shell as the
 * viewport changes — so a hit list living inside it would be lost every time
 * the window crossed a breakpoint, and `bind:this` would be reaching for a
 * component the shell had decided not to render.
 *
 * The state is a fact about the app; the panel is one way of looking at it.
 */
import { pick, type Candidate, type Hit, type Project } from '@gennetay/chart-core/lib/chartpick';
import { currentAt } from '@gennetay/chart-core/lib/currents';
import { tideAt } from '@gennetay/chart-core/lib/tidestations';
import { viewTime } from '@gennetay/chart-core/lib/viewtime.svelte';
import { layers } from '@gennetay/chart-core/map/layers.svelte';
import { currents } from '@gennetay/chart-layers/packs/tides/currents.svelte';
import { tideStations } from '@gennetay/chart-layers/packs/tides/tidestations.svelte';
import { tideStationsFeed } from '@gennetay/chart-layers/packs/tides/tidestationsfeed.svelte';
import { notesFeed, type NoteDetail } from '@gennetay/chart-layers/packs/poi/notes.svelte';
import { untrack } from 'svelte';

class Picked {
  hits = $state<Hit[]>([]);
  chosen = $state<Hit | null>(null);

  /* ═══ THE NOTE'S DETAIL, FETCHED WHEN ONE IS CHOSEN ═══
   *
   * The bbox list carries a name, a position and an icon — everything needed to
   * DRAW a pin and nothing worth reading — so the record is asked for on open
   * rather than for all seventy-five pins in view.
   *
   * The `seq` guard is not optional. Tapping through three notes in a busy
   * anchorage starts three requests and they do not come back in order; without
   * it the second reply can land after the third and fill the panel with the
   * wrong marina's fuel prices, which is exactly the kind of confident
   * wrongness that is worse than no answer. */
  noteDetail = $state<NoteDetail | null>(null);
  noteLoading = $state(false);
  #noteSeq = 0;

  /** Everything this host actually draws.
   *
   *  A candidate for something not on the chart would be pickable invisible
   *  water, which reads as the chart inventing things. Both tide regions are
   *  offered because both may be drawn — they are one layer twice. */
  #candidates(at: number): Candidate[] {
    const out: Candidate[] = [];
    for (const s of currents.stations) {
      const c = currentAt(s, at);
      if (!c) continue;
      out.push({
        kind: 'current',
        id: s.code,
        label: s.name,
        detail: `${Math.abs(c.kt).toFixed(1)} kn`,
        lat: s.lat,
        lon: s.lon
      });
    }
    for (const s of [...tideStations.stations, ...tideStationsFeed.stations]) {
      const t = tideAt(s, at);
      if (!t) continue;
      out.push({
        kind: 'tide',
        id: s.code,
        label: s.name,
        detail: t.rising ? '▲ making' : '▼ taking off',
        lat: s.lat,
        lon: s.lon
      });
    }
    /* GATED ON THE LAYER BEING VISIBLE. A pick that answered for a layer the
     * user has switched off is a menu about a chart they are not looking at.
     * `note` already ranks last in chartpick's tie-break, which is right:
     * somebody's opinion loses to every measurement under the same finger. */
    if (layers.isVisible('notes')) {
      for (const n of notesFeed.items) {
        out.push({
          kind: 'note',
          id: n.id,
          label: n.name || 'Note',
          detail: n.reviewCount ? `${n.rating?.toFixed(1)}/5 · ${n.reviewCount}` : undefined,
          lat: n.lat,
          lon: n.lon
        });
      }
    }
    return out;
  }

  /** `project` is passed in rather than the map, because that is all the pick
   *  needs — and it keeps this testable without a WebGL context. */
  tap(point: { x: number; y: number }, project: Project): boolean {
    const found = pick(point, this.#candidates(viewTime.now), project);
    this.hits = found;
    /* ONE HIT OPENS STRAIGHT AWAY. A list of one is a menu asking you to
     * confirm what you just pressed on. */
    this.chosen = found.length === 1 ? found[0]! : null;
    if (found.length) void this.#loadNote();
    return found.length > 0;
  }

  choose(h: Hit | null): void {
    this.chosen = h;
    void this.#loadNote();
  }

  clear(): void {
    this.hits = [];
    this.chosen = null;
    this.noteDetail = null;
    this.noteLoading = false;
  }

  async #loadNote(): Promise<void> {
    const hit = this.chosen;
    if (!hit || hit.kind !== 'note') {
      this.noteDetail = null;
      this.noteLoading = false;
      return;
    }
    const seq = ++this.#noteSeq;
    this.noteDetail = null;
    this.noteLoading = true;
    const d = await notesFeed.fetchNote(hit.id);
    if (seq !== this.#noteSeq) return;
    this.noteDetail = d;
    this.noteLoading = false;
  }

  /** The ANSWER, for the panel's peek line. */
  peek(): string {
    if (this.chosen === null) return `${this.hits.length} things here`;
    if (this.chosen.kind === 'current') {
      const st = currents.stations.find((s) => s.code === this.chosen!.id);
      const c = st ? currentAt(st, viewTime.now) : null;
      return c ? `${this.chosen.label} · ${Math.abs(c.kt).toFixed(1)} kn` : this.chosen.label;
    }
    return `${this.chosen.label} · ${this.chosen.detail ?? ''}`.trim();
  }
}

export const picked = new Picked();

/** Dispose a chart pick after another panel claims the surface.
 *
 * Called from an effect which tracks the panel selection. The picked fields
 * are deliberately untracked: `clear()` replaces the hits array, so tracking
 * that array here makes the disposal effect schedule itself again. */
export function clearPickedUnlessOpen(pickOpen: boolean): void {
  untrack(() => {
    if (!pickOpen && picked.hits.length) picked.clear();
  });
}
